import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { useOpenContactDialog } from '../contexts/ContactDialogContext';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { MAIN_BG_COLOR } from '../theme';
import MarketingFooterCta from '../components/MarketingFooterCta';
import type { ClientPageContent } from './clientPages';

const PAGE = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    sectionPaddingX: '1.25rem',
} as const;

type ClientPageProps = {
    content: ClientPageContent;
};

const ClientPage: React.FC<ClientPageProps> = ({ content }) => {
    const openContactDialog = useOpenContactDialog();

    return (
        <Box sx={{ width: '100%', background: '#fff', minHeight: '70vh' }}>
            <Box
                sx={{
                    width: '100%',
                    background: MAIN_BG_COLOR,
                    py: { xs: 8, md: 10 },
                    px: { xs: PAGE.sectionPaddingX, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        sx={{
                            fontFamily: PAGE.fontBody,
                            fontWeight: 400,
                            fontSize: '0.8125rem',
                            color: 'rgba(255,255,255,0.55)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            mb: 1.5,
                        }}
                    >
                        {content.eyebrow}
                    </Typography>
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: PAGE.fontHeading,
                            fontWeight: 800,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            lineHeight: 1.15,
                            color: '#fff',
                            mb: 2,
                            maxWidth: 800,
                        }}
                    >
                        {content.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: PAGE.fontBody,
                            fontWeight: 400,
                            fontSize: { xs: '1.0625rem', md: '1.125rem' },
                            lineHeight: 1.55,
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 640,
                            mb: 4,
                        }}
                    >
                        {content.explainer}
                    </Typography>
                    <Button
                        onClick={() => {
                            analytics.track(AnalyticsEvent.CLICK_REQUEST_DEMO, {
                                location: `client_${content.slug}`,
                            });
                            openContactDialog();
                        }}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            background: '#fff',
                            color: MAIN_BG_COLOR,
                            borderRadius: '100px',
                            px: 2.5,
                            py: 1,
                            fontFamily: PAGE.fontBody,
                            fontWeight: 500,
                            textTransform: 'none',
                            '&:hover': { background: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Agenda una demo
                    </Button>
                </Container>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    py: { xs: 6, md: 8 },
                    px: { xs: PAGE.sectionPaddingX, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                        }}
                    >
                        {content.solutions.map(({ id, label, description, to, Icon }) => (
                            <Box
                                key={id}
                                component={Link}
                                to={to}
                                sx={{
                                    background: '#f4f4f5',
                                    borderRadius: '12px',
                                    p: { xs: 3, md: 3.5 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    textAlign: 'left',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    transition: 'background 0.15s ease',
                                    '&:hover': {
                                        background: '#ececee',
                                    },
                                }}
                            >
                                <Icon sx={{ color: MAIN_BG_COLOR, fontSize: 28, mb: 2 }} />
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: PAGE.fontBody,
                                        fontWeight: 700,
                                        fontSize: '1.125rem',
                                        lineHeight: 1.3,
                                        color: MAIN_BG_COLOR,
                                        mb: 1,
                                    }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: PAGE.fontBody,
                                        fontWeight: 400,
                                        fontSize: '0.9875rem',
                                        lineHeight: 1.55,
                                        color: 'rgba(0,0,0,0.55)',
                                    }}
                                >
                                    {description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            <MarketingFooterCta
                title={content.footerCta.title}
                ctaLabel={content.footerCta.ctaLabel}
                ctaTo={content.footerCta.ctaTo}
                analyticsLocation={`client_footer_${content.slug}`}
            />
        </Box>
    );
};

export default ClientPage;
